import { useEffect, useState } from "react";
import { IdCard } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { updateClientById } from "../config/url";
import { qrDataUrl } from "../utils/qrDataUrl";
import { downloadMemberCardPdf } from "../utils/memberCardPrint";
import classes from "../pages/ClientDetails.module.css";

const formatIssuedDate = (value) => {
  if (!value) return "—";
  const raw = String(value);
  const date = new Date(raw.includes("T") ? raw : `${raw}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const CARD_STATUS_LABELS = {
  active: "Active",
  inactive: "Deactivated",
  lost: "Lost",
  replaced: "Replaced",
  revoked: "Revoked",
};

const getCardStatusLabel = (status) => {
  const key = String(status || "").trim().toLowerCase();
  if (!key) return "Not Issued";
  return CARD_STATUS_LABELS[key] || status;
};

const getCardStatusTextClass = (status) => {
  const key = String(status || "").trim().toLowerCase();
  if (!key) return classes.cardDetailStatusMuted;
  if (key === "active") return classes.cardDetailStatusActive;
  if (key === "inactive" || key === "revoked") return classes.cardDetailStatusDanger;
  if (key === "lost") return classes.cardDetailStatusWarning;
  return classes.cardDetailStatusMuted;
};

const isCardActive = (card) => String(card?.status || "").toLowerCase() === "active";

const CardVisual = ({ clientActive = true, clientName, memberCard, qrImage, variant = "preview" }) => {
  const active = isCardActive(memberCard) && clientActive;
  return (
    <div
      className={`${classes.physicalCard} ${
        active ? "" : classes.physicalCardDisabled
      } ${variant === "print" ? classes.physicalCardPrint : ""}`.trim()}
    >
      <div className={classes.physicalCardTop}>
        <span className={classes.physicalCardBrand}>TeleTrace</span>
        <span className={classes.physicalCardEyebrow}>MEMBER CARD</span>
      </div>
      <div className={classes.physicalCardBody}>
        <div className={classes.physicalCardCopy}>
          <div className={classes.physicalCardName}>{clientName}</div>
          <div className={classes.physicalCardId}>
            ID: {memberCard?.publicReference || "—"}
          </div>
          <div className={classes.physicalCardHint}>Scan to verify visit</div>
        </div>
        <div className={classes.physicalCardQr}>
          {qrImage ? (
            <img alt="" className={classes.physicalCardQrImage} src={qrImage} />
          ) : (
            <div className={classes.physicalCardQrPlaceholder} />
          )}
        </div>
      </div>
      {active ? null : (
        <div className={classes.deactivatedStamp} aria-hidden="true">
          {!clientActive || String(memberCard?.status || "").toLowerCase() === "inactive"
            ? "DEACTIVATED"
            : getCardStatusLabel(memberCard?.status)}
        </div>
      )}
    </div>
  );
};

const ConfirmModal = ({
  actions,
  children,
  onClose,
  saving,
  title,
}) => (
  <div className={classes.modalRoot} role="presentation">
    <button
      aria-label="Close dialog"
      className={classes.modalOverlay}
      disabled={saving}
      onClick={onClose}
      type="button"
    />
    <div className={classes.modalCard} role="dialog">
      <h3 className={classes.modalTitle}>{title}</h3>
      <div className={classes.modalText}>{children}</div>
      <div className={classes.modalActions}>{actions}</div>
    </div>
  </div>
);

const MemberCardSection = ({
  client,
  clientName,
  clientActive,
  onClientUpdate,
  requestConfig,
}) => {
  const memberCard = client?.memberCard || null;
  const eligibility = client?.memberCardEligibility || {};
  const warningMessage =
    eligibility.message ||
    (!clientActive ? "Client must be active to issue a card." : null);
  const restricted = Boolean(warningMessage);
  const cardStatus = memberCard?.status || "";
  const cardActive = isCardActive(memberCard) && !restricted;
  const canIssue = !restricted && (eligibility.canIssue ?? Boolean(clientActive && !isCardActive(memberCard)));
  const canReissue = !restricted && (eligibility.canReissue ?? Boolean(clientActive && cardActive));
  const canDeactivate = !restricted && (eligibility.canDeactivate ?? cardActive);
  const canPrint = !restricted && (eligibility.canPrint ?? cardActive);

  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [qrImage, setQrImage] = useState("");
  const [printQrImage, setPrintQrImage] = useState("");

  useEffect(() => {
    let cancelled = false;
    const loadQr = async () => {
      if (!memberCard?.qrToken || !cardActive) {
        setQrImage("");
        return;
      }
      try {
        const src = await qrDataUrl(memberCard.qrToken, 220);
        if (!cancelled) setQrImage(src);
      } catch {
        if (!cancelled) setQrImage("");
      }
    };
    loadQr();
    return () => {
      cancelled = true;
    };
  }, [cardActive, memberCard?.qrToken]);

  useEffect(() => {
    let cancelled = false;
    const loadPrintQr = async () => {
      if (modal !== "print" || !memberCard?.qrToken) {
        setPrintQrImage("");
        return;
      }
      try {
        const src = await qrDataUrl(memberCard.qrToken, 280);
        if (!cancelled) setPrintQrImage(src);
      } catch {
        if (!cancelled) setPrintQrImage("");
      }
    };
    loadPrintQr();
    return () => {
      cancelled = true;
    };
  }, [memberCard?.qrToken, modal]);

  const applyCardResponse = (res) => {
    const card = res.data?.memberCard?.card;
    if (!card) return;
    onClientUpdate({
      memberCard: {
        ...card,
        qrToken: res.data?.memberCard?.qrToken || card.qrToken || null,
      },
    });
  };

  const runCardAction = async (body, successMessage) => {
    setSaving(true);
    try {
      const res = await axios.put(updateClientById(client.id), body, requestConfig);
      applyCardResponse(res);
      await onClientUpdate({ reload: true });
      setModal(null);
      if (body.issueMemberCard && res.data?.memberCard?.alreadyIssued) {
        toast.info("An active Member Card already exists for this client.");
      } else {
        toast.success(successMessage);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to update Member Card.");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!cardActive || !memberCard) return;
    try {
      await downloadMemberCardPdf({
        card: memberCard,
        clientName,
      });
    } catch (err) {
      toast.error("Unable to download Member Card.");
    }
  };

  return (
    <>
      <div className={classes.memberCardLayout}>
        <section className={classes.memberPanel}>
          <h3 className={classes.memberPanelTitle}>Card Preview</h3>
          {memberCard ? (
            <div
              className={
                cardActive ? classes.cardPreviewIssued : classes.cardPreviewDisabled
              }
            >
              <CardVisual
                clientActive={clientActive}
                clientName={clientName}
                memberCard={memberCard}
                qrImage={cardActive ? qrImage : ""}
              />
            </div>
          ) : (
            <div className={classes.cardPreviewEmpty}>
              <IdCard color="#c5ced6" size={42} strokeWidth={1.5} />
              <p className={classes.cardPreviewEmptyText}>No card issued</p>
            </div>
          )}
        </section>

        <section className={classes.memberPanel}>
          <h3 className={classes.memberPanelTitle}>Card Details</h3>
          <div className={classes.cardDetailRows}>
            <div className={classes.cardDetailRow}>
              <span className={classes.cardDetailLabel}>Card ID</span>
              <span className={`${classes.cardDetailValue} ${restricted ? classes.cardDetailValueMuted : ""}`}>
                {memberCard?.publicReference || "—"}
              </span>
            </div>
            <div className={classes.cardDetailRow}>
              <span className={classes.cardDetailLabel}>Date Issued</span>
              <span className={`${classes.cardDetailValue} ${restricted ? classes.cardDetailValueMuted : ""}`}>
                {memberCard ? formatIssuedDate(memberCard.issuedAt) : "—"}
              </span>
            </div>
            <div className={classes.cardDetailRow}>
              <span className={classes.cardDetailLabel}>Status</span>
              <span className={`${classes.cardDetailStatus} ${getCardStatusTextClass(restricted && memberCard ? "inactive" : cardStatus)}`}>
                {restricted && memberCard ? "Deactivated" : getCardStatusLabel(cardStatus)}
              </span>
            </div>
          </div>

          {canIssue ? (
            <button
              className={classes.issueCardButton}
              onClick={() => setModal("issue")}
              type="button"
            >
              {memberCard ? "Issue New Card" : "Issue Card"}
            </button>
          ) : null}

          {cardActive ? (
            <div className={classes.cardActionRow}>
              {canPrint ? (
                <button
                  className={classes.issueCardButton}
                  onClick={() => setModal("print")}
                  type="button"
                >
                  Print Card
                </button>
              ) : null}
              {canReissue ? (
                <button
                  className={classes.cardGhostButton}
                  onClick={() => setModal("reissue")}
                  type="button"
                >
                  Reissue
                </button>
              ) : null}
              {canDeactivate ? (
                <button
                  className={classes.cardDangerButton}
                  onClick={() => setModal("deactivateCard")}
                  type="button"
                >
                  Deactivate
                </button>
              ) : null}
            </div>
          ) : null}

          {warningMessage ? (
            <div className={classes.issueCardWarning} role="alert">
              {warningMessage}
            </div>
          ) : null}
        </section>
      </div>

      {modal === "issue" ? (
        <ConfirmModal
          onClose={() => !saving && setModal(null)}
          saving={saving}
          title="Issue Member Card"
          actions={
            <>
              <button
                className={classes.modalCancel}
                disabled={saving}
                onClick={() => setModal(null)}
                type="button"
              >
                Cancel
              </button>
              <button
                className={classes.modalPrimary}
                disabled={saving}
                onClick={() =>
                  runCardAction({ issueMemberCard: true }, "Member Card issued.")
                }
                type="button"
              >
                {saving ? "Issuing..." : "Issue Card"}
              </button>
            </>
          }
        >
          A TeleTrace Member Card will be issued to <strong>{clientName}</strong>.
          Their assigned provider can scan it at the end of a visit to confirm
          service delivery.
        </ConfirmModal>
      ) : null}

      {modal === "reissue" ? (
        <ConfirmModal
          onClose={() => !saving && setModal(null)}
          saving={saving}
          title="Reissue Member Card"
          actions={
            <>
              <button
                className={classes.modalCancel}
                disabled={saving}
                onClick={() => setModal(null)}
                type="button"
              >
                Cancel
              </button>
              <button
                className={classes.modalPrimary}
                disabled={saving}
                onClick={() =>
                  runCardAction({ reissueMemberCard: true }, "Member Card reissued.")
                }
                type="button"
              >
                {saving ? "Reissuing..." : "Reissue Card"}
              </button>
            </>
          }
        >
          A replacement card will be issued for <strong>{clientName}</strong>. The
          previous card ID will be deactivated immediately.
        </ConfirmModal>
      ) : null}

      {modal === "deactivateCard" ? (
        <ConfirmModal
          onClose={() => !saving && setModal(null)}
          saving={saving}
          title="Deactivate Member Card"
          actions={
            <>
              <button
                className={classes.modalCancel}
                disabled={saving}
                onClick={() => setModal(null)}
                type="button"
              >
                Cancel
              </button>
              <button
                className={classes.modalDeactivate}
                disabled={saving}
                onClick={() =>
                  runCardAction(
                    { deactivateMemberCard: true },
                    "Member Card deactivated."
                  )
                }
                type="button"
              >
                {saving ? "Deactivating..." : "Deactivate"}
              </button>
            </>
          }
        >
          Are you sure you want to deactivate <strong>{clientName}&apos;s</strong>{" "}
          Member Card? {clientName} will need a new card issued to resume this
          confirmation method.
        </ConfirmModal>
      ) : null}

      {modal === "print" ? (
        <div className={classes.modalRoot} role="presentation">
          <button
            aria-label="Close print preview"
            className={classes.modalOverlay}
            onClick={() => setModal(null)}
            type="button"
          />
          <div
            aria-labelledby="print-ready-card-title"
            className={classes.printModalCard}
            role="dialog"
          >
            <h3 className={classes.printModalTitle} id="print-ready-card-title">
              Print-Ready Card
            </h3>
            <div className={classes.printModalDivider} />
            <p className={classes.printModalCaption}>
              Card is CR80 format (3.375&quot; × 2.125&quot;)
            </p>
            <div className={classes.printPreviewWrap}>
              <CardVisual
                clientName={clientName}
                memberCard={memberCard}
                qrImage={printQrImage || qrImage}
                variant="print"
              />
            </div>
            <div className={classes.printMeta}>
              <div className={classes.printMetaRow}>
                <span className={classes.printMetaLabel}>Format:</span>
                <span className={classes.printMetaValue}>
                  CR80 (3.375&quot; × 2.125&quot;)
                </span>
              </div>
              <div className={classes.printMetaRow}>
                <span className={classes.printMetaLabel}>File type:</span>
                <span className={classes.printMetaValue}>High-Res PDF</span>
              </div>
              <div className={classes.printMetaRow}>
                <span className={classes.printMetaLabel}>Suggested printing:</span>
                <span className={classes.printMetaValue}>
                  Local print shop or ID card printer
                </span>
              </div>
            </div>
            <div className={classes.printModalActions}>
              <button
                className={classes.modalCancel}
                onClick={() => setModal(null)}
                type="button"
              >
                Close
              </button>
              <button
                className={classes.modalPrimary}
                onClick={handleDownload}
                type="button"
              >
                Download • Print
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default MemberCardSection;
